import React, { Fragment, Component } from 'react';
import { Button, Card, Group, Text, Title, Badge, Divider, Table, Accordion, List } from '@mantine/core';
import { ArrowLeft, LineChart, Clock, Utensils, Dumbbell, Microscope, Droplet, Apple, Brain, Bookmark } from 'lucide-react';
import { ProtocolRadarChart } from './ProtocolRadarChart';
import { protocolAxes } from '../data/protocolData';
import { Toolbelt } from './Toolbelt';
export function ProtocolDashboard({
  protocol,
  onReset
}) {
  if (!protocol) return null;
  // Split the protocol name into its components
  let [namePart1, namePart2] = protocol.name.split(' | ');
  // Handle Identity None case - remove "Standard" from the name
  if (protocol.selections.identity === 'None') {
    namePart1 = namePart1.replace('Standard', '').trim();
  }
  // Capitalize the first letter of the second word in namePart1
  const namePart1Words = namePart1.split(' ');
  if (namePart1Words.length >= 2) {
    namePart1Words[1] = namePart1Words[1].charAt(0).toUpperCase() + namePart1Words[1].slice(1);
    namePart1 = namePart1Words.join(' ');
  }
  // Map the raw keys to display-friendly formatted names
  const keyDisplayNames = {
    identity: 'Identity',
    mealFrequency: 'Meal Frequency',
    foodType: 'Food Type',
    macroStrategy: 'Macro Strategy',
    microStrategy: 'Micro Strategy',
    feedingCycle: 'Feeding Cycle',
    exerciseTiming: 'Exercise Timing'
  };
  const universalPrinciples = [{
    number: 1,
    principle: 'Hydrate Generously',
    description: 'Keep total fluid-electrolyte intake high enough that urine is pale, thirst is minimal, and performance markers (HRV, endurance) stay stable.',
    implementation: ['≥ 30-35 mL water / kg body-weight daily', 'Add 1–2 g salt or balanced electrolyte mix per liter during heavy sweat or fasting', 'Drink half your body weight (lbs) in ounces of water daily'],
    icon: <Droplet size={16} className="text-blue-500" data-id="element-256" />
  }, {
    number: 2,
    principle: 'Prioritize Food Quality',
    description: 'Choose the least-adulterated, most nutrient-dense version of every ingredient—thereby automating good fat profile, lowering chemical load, and raising vitamin/mineral density.',
    implementation: ['Prefer mono- & omega-3 fats over seed-oil heavies', 'Organic / pasture / wild when feasible', 'Store & cook to avoid plastics, rancid oils, charred residues'],
    icon: <Apple size={16} className="text-green-500" data-id="element-257" />
  }, {
    number: 3,
    principle: 'Eat Mindfully & Slowly',
    description: 'Bring full attention to each meal: chew thoroughly, pause between bites, and stop at comfortable satiety—improving digestion, hormonal signaling, and emotional relationship with food.',
    implementation: ['15-20 min minimum per meal', 'No screens while eating', '20–30 chews per bite rule-of-thumb'],
    icon: <Brain size={16} className="text-purple-500" data-id="element-258" />
  }];
  // Handler functions for Toolbelt actions
  const handleScreenshot = () => {
    // This is now implemented in the Toolbelt component
  };
  const handleSavePDF = () => {
    // This is now implemented in the Toolbelt component
  };
  const handleEmail = () => {
    // This is now implemented in the Toolbelt component
  };
  // Handler to save the protocol to localStorage
  const handleSaveProtocol = () => {
    // Generate a unique ID for the saved vector
    const id = `vector-${Date.now()}`;
    // Create the saved vector object
    const savedVector = {
      id,
      name: protocol.name,
      protocol,
      savedAt: new Date().toISOString()
    };
    // Get existing saved vectors from localStorage
    const existingSavedVectors = localStorage.getItem('nutrivector-saved');
    let savedVectors = [];
    if (existingSavedVectors) {
      savedVectors = JSON.parse(existingSavedVectors);
    }
    // Add the new vector to the array
    savedVectors.push(savedVector);
    // Save back to localStorage
    localStorage.setItem('nutrivector-saved', JSON.stringify(savedVectors));
    // Show a confirmation to the user
    alert('Protocol saved successfully!');
  };
  return <div data-id="element-259">
      <Card shadow="sm" p="xl" radius="md" className="bg-white mb-8 protocol-card" data-id="element-260">
        <div className="text-center mb-6" data-id="element-261">
          <Badge size="lg" color="teal" variant="light" className="mb-4" data-id="element-262">
            Your Personalized Protocol
          </Badge>
          <Title order={2} className="text-3xl font-bold text-slate-800 mb-2" data-id="element-263">
            {namePart1}
          </Title>
          <Title order={3} className="text-xl font-medium text-emerald-600 mb-4" data-id="element-264">
            Fitnut: {namePart2}
          </Title>
        </div>
        <Divider my="md" data-id="element-265" />
        <Group className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-266">
          {Object.entries(protocol.selections).map(([key, value]) => <Badge key={key} size="lg" color="gray" variant="outline" data-id="element-267">
              {value}
            </Badge>)}
        </Group>
        <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="element-268">
          <div data-id="element-269">
            <Accordion defaultValue="protocol-summary" data-id="element-270">
              <Accordion.Item value="protocol-summary" data-id="element-271">
                <Accordion.Control icon={<Utensils size={16} data-id="element-273" />} data-id="element-272">
                  <Text fw={500} className="uppercase text-xs tracking-wider" data-id="element-274">
                    Protocol Summary
                  </Text>
                </Accordion.Control>
                <Accordion.Panel data-id="element-275">
                  <Table data-id="element-276">
                    <Table.Tbody data-id="element-277">
                      {Object.entries(protocol.selections).map(([key, value]) => <Table.Tr key={key} data-id="element-278">
                            <Table.Td className="font-medium text-slate-700" data-id="element-279">
                              {keyDisplayNames[key] || key}
                            </Table.Td>
                            <Table.Td className="text-slate-600" data-id="element-280">
                              {value}
                            </Table.Td>
                          </Table.Tr>)}
                    </Table.Tbody>
                  </Table>
                </Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="timing-details" data-id="element-281">
                <Accordion.Control icon={<Clock size={16} data-id="element-283" />} data-id="element-282">
                  <Text fw={500} className="uppercase text-xs tracking-wider" data-id="element-284">
                    Timing & Cycles
                  </Text>
                </Accordion.Control>
                <Accordion.Panel data-id="element-285">
                  <ul className="list-disc pl-5 space-y-2" data-id="element-286">
                    {protocol.selections.mealFrequency && <li data-id="element-287">
                        <Text size="sm" className="text-slate-600" data-id="element-288">
                          <span className="font-medium" data-id="element-289">
                            {keyDisplayNames.mealFrequency}:
                          </span>{' '}
                          {protocol.selections.mealFrequency} —{' '}
                          {protocolAxes.find(axis => axis.id === 'mealFrequency')?.options.find(opt => opt.label === protocol.selections.mealFrequency)?.description}
                        </Text>
                      </li>}
                    {protocol.selections.feedingCycle && <li data-id="element-290">
                        <Text size="sm" className="text-slate-600" data-id="element-291">
                          <span className="font-medium" data-id="element-292">
                            {keyDisplayNames.feedingCycle}:
                          </span>{' '}
                          {protocol.selections.feedingCycle} —{' '}
                          {protocolAxes.find(axis => axis.id === 'feedingCycle')?.options.find(opt => opt.label === protocol.selections.feedingCycle)?.description}
                        </Text>
                      </li>}
                    {protocol.selections.exerciseTiming && <li data-id="element-293">
                        <Text size="sm" className="text-slate-600" data-id="element-294">
                          <span className="font-medium" data-id="element-295">
                            {keyDisplayNames.exerciseTiming}:
                          </span>{' '}
                          {protocol.selections.exerciseTiming} —{' '}
                          {protocolAxes.find(axis => axis.id === 'exerciseTiming')?.options.find(opt => opt.label === protocol.selections.exerciseTiming)?.description}
                        </Text>
                      </li>}
                  </ul>
                </Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="nutrition-details" data-id="element-296">
                <Accordion.Control icon={<Microscope size={16} data-id="element-298" />} data-id="element-297">
                  <Text fw={500} className="uppercase text-xs tracking-wider" data-id="element-299">
                    Nutritional Approach
                  </Text>
                </Accordion.Control>
                <Accordion.Panel data-id="element-300">
                  <ul className="list-disc pl-5 space-y-2" data-id="element-301">
                    {protocol.selections.foodType && <li data-id="element-302">
                        <Text size="sm" className="text-slate-600" data-id="element-303">
                          <span className="font-medium" data-id="element-304">
                            {keyDisplayNames.foodType}:
                          </span>{' '}
                          {protocol.selections.foodType} —{' '}
                          {protocolAxes.find(axis => axis.id === 'foodType')?.options.find(opt => opt.label === protocol.selections.foodType)?.description}
                        </Text>
                      </li>}
                    {protocol.selections.macroStrategy && <li data-id="element-305">
                        <Text size="sm" className="text-slate-600" data-id="element-306">
                          <span className="font-medium" data-id="element-307">
                            {keyDisplayNames.macroStrategy}:
                          </span>{' '}
                          {protocol.selections.macroStrategy} —{' '}
                          {protocolAxes.find(axis => axis.id === 'macroStrategy')?.options.find(opt => opt.label === protocol.selections.macroStrategy)?.description}
                        </Text>
                      </li>}
                    {protocol.selections.microStrategy && <li data-id="element-308">
                        <Text size="sm" className="text-slate-600" data-id="element-309">
                          <span className="font-medium" data-id="element-310">
                            {keyDisplayNames.microStrategy}:
                          </span>{' '}
                          {protocol.selections.microStrategy} —{' '}
                          {protocolAxes.find(axis => axis.id === 'microStrategy')?.options.find(opt => opt.label === protocol.selections.microStrategy)?.description}
                        </Text>
                      </li>}
                  </ul>
                </Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="fitness-details" data-id="element-311">
                <Accordion.Control icon={<Dumbbell size={16} data-id="element-313" />} data-id="element-312">
                  <Text fw={500} className="uppercase text-xs tracking-wider" data-id="element-314">
                    Fitness Integration
                  </Text>
                </Accordion.Control>
                <Accordion.Panel data-id="element-315">
                  <ul className="list-disc pl-5 space-y-2" data-id="element-316">
                    {protocol.selections.exerciseTiming && <li data-id="element-317">
                        <Text size="sm" className="text-slate-600" data-id="element-318">
                          Your protocol is designed to support{' '}
                          {protocol.selections.exerciseTiming} exercise timing (
                          {protocolAxes.find(axis => axis.id === 'exerciseTiming')?.options.find(opt => opt.label === protocol.selections.exerciseTiming)?.description}
                          ), optimizing nutrient partitioning for performance
                          and recovery.
                        </Text>
                      </li>}
                    {protocol.selections.identity && <li data-id="element-319">
                        <Text size="sm" className="text-slate-600" data-id="element-320">
                          <span className="font-medium" data-id="element-321">
                            {keyDisplayNames.identity}:
                          </span>{' '}
                          {protocol.selections.identity} —{' '}
                          {protocolAxes.find(axis => axis.id === 'identity')?.options.find(opt => opt.label === protocol.selections.identity)?.description}
                        </Text>
                      </li>}
                  </ul>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </div>
          <div className="flex flex-col justify-center items-center" data-id="element-322">
            <div className="flex items-center mb-2" data-id="element-323">
              <LineChart size={18} className="text-emerald-600 mr-2" data-id="element-324" />
              <Title order={4} className="text-slate-700" data-id="element-325">
                Protocol Performance Profile
              </Title>
            </div>
            <Text size="sm" className="text-slate-500 mb-4 text-center" data-id="element-326">
              Predicted optimization across 12 health dimensions
            </Text>
            <ProtocolRadarChart data={protocol.radarData} data-id="element-327" />
          </div>
        </div>
        <div className="bg-slate-100 p-6 rounded-lg mb-6" data-id="element-328">
          <Title order={4} className="mb-4 text-slate-700 text-center" data-id="element-329">
            Three Universal Principles
            <Text size="sm" className="text-slate-500 mt-1" data-id="element-330">
              (Beyond Any Specific Diet Stack)
            </Text>
          </Title>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-331">
            {universalPrinciples.map(principle => <div key={principle.number} className="bg-white p-4 rounded-md shadow-sm" data-id="element-332">
                <div className="flex items-center mb-2" data-id="element-333">
                  {principle.icon}
                  <Title order={5} className="ml-2 text-slate-800" data-id="element-334">
                    {principle.number}. {principle.principle}
                  </Title>
                </div>
                <Text size="sm" className="text-slate-600 mb-3" data-id="element-335">
                  {principle.description}
                </Text>
                <div className="bg-slate-50 p-3 rounded-md" data-id="element-336">
                  <Text size="sm" fw={500} className="text-slate-700 mb-1" data-id="element-337">
                    Quick Implementation:
                  </Text>
                  <ol className="pl-5 text-sm text-slate-600 space-y-1" data-id="element-338">
                    {principle.implementation.map((item, idx) => <li key={idx} data-id="element-339">{item}</li>)}
                  </ol>
                </div>
              </div>)}
          </div>
        </div>
        <Group justify="center" mt="xl" data-id="element-340">
          <Button variant="light" leftSection={<ArrowLeft size={16} data-id="element-342" />} onClick={onReset} data-id="element-341">
            Start Over
          </Button>
          <Button variant="filled" leftSection={<Bookmark size={16} data-id="element-344" />} onClick={handleSaveProtocol} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-343">
            Save Vector
          </Button>
        </Group>
      </Card>
      <Toolbelt onScreenshot={handleScreenshot} onSavePDF={handleSavePDF} onEmail={handleEmail} data-id="element-345" />
    </div>;
}